import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { activitiesApi, workersApi, filesApi, formTemplatesApi, projectFormsApi, documentFoldersApi } from '../services/api';
import { compressImages } from '../utils/imageCompression';
import { limitFilesForUpload } from '../utils/uploadLimits';
import { renameFile } from '../utils/fileNaming';
import ImageViewer from './ImageViewer';
import ActivityCard from './ActivityCard';
import ActivityDialog from './ActivityDialog';

const byDueDate = (first, second) => {
  const firstCompleted = Boolean(first.is_completed ?? first.completed);
  const secondCompleted = Boolean(second.is_completed ?? second.completed);
  if (firstCompleted !== secondCompleted) return firstCompleted ? 1 : -1;
  const firstDue = first.due_date ? new Date(first.due_date).getTime() : Infinity;
  const secondDue = second.due_date ? new Date(second.due_date).getTime() : Infinity;
  const firstTime = Number.isNaN(firstDue) ? Infinity : firstDue;
  const secondTime = Number.isNaN(secondDue) ? Infinity : secondDue;
  return firstTime === secondTime ? 0 : firstTime - secondTime;
};

const ProjectActivitiesTab = ({ projectId, initialActivityId = null, onOpenForm }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State management
  const [activities, setActivities] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [completingActivity, setCompletingActivity] = useState(null);
  
  // Viewer state for linked plans
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [viewerPin, setViewerPin] = useState(null);
  const [viewerPins, setViewerPins] = useState([]);
  const [, setViewerLoading] = useState(false);
  const [loadingPlanActivityId, setLoadingPlanActivityId] = useState(null);
  const autoExpandedActivityRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: null,
    is_completed: false,
    assigned_workers: [],
    linked_forms: [],
    pending_form_templates: [],
    pending_existing_forms: [],
    checklist_items: [],
  });
  
  // Activity notes and photos state
  const [activityNotes, setActivityNotes] = useState({});
  const [activityPhotos, setActivityPhotos] = useState({});
  const [loadingNotes] = useState({});
  const [loadingPhotos] = useState({});
  const [newNotes, setNewNotes] = useState({}); // Changed to object to store notes per activity
  const [selectedPhotos, setSelectedPhotos] = useState({});
  const [photoPreview, setPhotoPreview] = useState({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 });
  const [uploading, setUploading] = useState({}); // Simple loading state per activity
  
  // Dialog photos state
  const [dialogPhotos, setDialogPhotos] = useState([]);
  const [pinMode, setPinMode] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [formTemplates, setFormTemplates] = useState([]);
  const [projectForms, setProjectForms] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const [documentFolders, setDocumentFolders] = useState([]);
  const [projectFilesLoading, setProjectFilesLoading] = useState(false);

  // Worker mention state
  const [mentionSuggestions, setMentionSuggestions] = useState({});
  const [showMentions, setShowMentions] = useState({});
  const pendingChecklistSavesRef = useRef(new Map());
  
  const refreshActivities = useCallback(async () => {
    try {
      setLoading(true);
      // Get activities with notes pre-loaded in a single query
      const response = await activitiesApi.getByProjectId(projectId, undefined, true);
      setActivities(response.data);
      
      // Pre-populate notes and photos state from the response
      const notesData = {};
      const photosData = {};
      response.data.forEach(activity => {
        if (activity.activity_notes && activity.activity_notes.length > 0) {
          notesData[activity.activity_id] = activity.activity_notes;
        }
        if (activity.photos && activity.photos.length > 0) {
          photosData[activity.activity_id] = activity.photos;
        }
      });
      setActivityNotes(notesData);
      setActivityPhotos(photosData);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadWorkers = useCallback(async () => {
    if (!projectId) {
      console.log('loadWorkers skipped: No projectId');
      return;
    }
    console.log('loadWorkers called for projectId:', projectId);
    try {
      const response = await workersApi.getByProjectId(projectId);
      console.log('Workers loaded:', response.data);
      setWorkers(response.data);
    } catch (error) {
      console.error('Error loading workers:', error);
    }
  }, [projectId]);

  const loadProjectForms = useCallback(async () => {
    if (!projectId) {
      return;
    }

    try {
      const response = await projectFormsApi.getByProjectId(projectId, { summary: true });
      setProjectForms(response.data || []);
    } catch (error) {
      console.error('Error loading project forms:', error);
    }
  }, [projectId]);

  useEffect(() => {
    if (!dialogOpen || !projectId) return;
    let cancelled = false;
    setProjectFilesLoading(true);
    Promise.all([
      filesApi.getByProjectId(projectId, 'plans'),
      filesApi.getByProjectId(projectId, 'photos'),
      documentFoldersApi.getByProjectId(projectId),
    ]).then(([plansResponse, photosResponse, foldersResponse]) => {
      if (!cancelled) {
        setProjectFiles([...(plansResponse.data || []), ...(photosResponse.data || [])]);
        setDocumentFolders(foldersResponse.data || []);
      }
    }).catch((error) => console.error('Error loading project attachments:', error))
      .finally(() => { if (!cancelled) setProjectFilesLoading(false); });
    return () => { cancelled = true; };
  }, [dialogOpen, projectId]);

  useEffect(() => {
    if (projectId) {
      // Load activities and workers in parallel for better performance
      Promise.all([
        refreshActivities(),
        loadWorkers(),
        formTemplatesApi.getAll().then(response => setFormTemplates(response.data || [])),
        loadProjectForms()
      ]).catch(error => {
        console.error('Error during parallel loading:', error);
      });
    }
  }, [projectId, refreshActivities, loadWorkers, loadProjectForms]);

  const syncChecklistItemsInState = useCallback((activityId, checklistItems) => {
    setActivities((prevActivities) =>
      prevActivities.map((currentActivity) =>
        currentActivity.activity_id === activityId
          ? { ...currentActivity, checklist_items: checklistItems }
          : currentActivity
      )
    );

    setSelectedActivity((prevActivity) =>
      prevActivity?.activity_id === activityId
        ? { ...prevActivity, checklist_items: checklistItems }
        : prevActivity
    );

    setViewerPin((prevPin) =>
      prevPin?.activity_id === activityId
        ? { ...prevPin, checklist_items: checklistItems }
        : prevPin
    );

    setViewerPins((prevPins) =>
      prevPins.map((pin) =>
        pin.activity_id === activityId
          ? { ...pin, checklist_items: checklistItems }
          : pin
      )
    );
  }, []);

  const flushPendingChecklistSaves = useCallback(() => {
    pendingChecklistSavesRef.current.forEach((payload, activityId) => {
      void activitiesApi.updateKeepalive(activityId, payload).catch((error) => {
        console.error('Error flushing activity checklist update:', error);
      });
    });
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      flushPendingChecklistSaves();
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      flushPendingChecklistSaves();
    };
  }, [flushPendingChecklistSaves]);

  // Activity notes and photos are pre-loaded from the initial query
  // No need for individual load functions

  // Auto-expand activity when initialActivityId is provided (e.g., from notification).
  // Only do this once per returned activity ID so the user can still collapse it afterwards.
  useEffect(() => {
    if (!initialActivityId || activities.length === 0) {
      return;
    }

    const nextActivityId = String(initialActivityId);
    if (autoExpandedActivityRef.current === nextActivityId) {
      return;
    }

    const activityExists = activities.some((activity) => String(activity.activity_id) === nextActivityId);
    if (!activityExists) {
      return;
    }

    autoExpandedActivityRef.current = nextActivityId;
    setExpandedActivity(initialActivityId);

    setTimeout(() => {
      const activityElement = document.getElementById(`activity-${initialActivityId}`);
      if (activityElement) {
        activityElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  }, [initialActivityId, activities]);

  const handleOpenDialog = (activity = null, initialData = null) => {
    console.log('handleOpenDialog called');
    console.log('Workers array:', workers);
    console.log('Workers length:', workers?.length);
    console.log('Activity:', activity);
    
    // Ensure workers are loaded
    if (!workers || workers.length === 0) {
      console.log('Workers empty in handleOpenDialog, reloading...');
      loadWorkers();
    }
    
    if (activity) {
      setSelectedActivity(activity);
      setFormData({
        title: activity.title,
        description: activity.description || '',
        due_date: activity.due_date ? new Date(activity.due_date) : null,
        is_completed: activity.is_completed,
        assigned_workers: (activity.assigned_workers || [])
          .map(aw => aw?.worker_id)
          .filter(id => id != null),
        source_file_id: activity.source_file_id || activity.SourceFileId,
        pin_x: activity.pin_x || activity.PinX,
        pin_y: activity.pin_y || activity.PinY,
        existing_photos: activity.photos || [],
        existing_documents: activity.documents || [],
        linked_forms: activity.forms || [],
        pending_form_templates: [],
        pending_existing_forms: [],
        pending_file_links: [],
        removed_photo_ids: [],
        removed_document_ids: [],
        removed_form_ids: [],
        checklist_items: activity.checklist_items || [],
      });
    } else {
      setSelectedActivity(null);
      setFormData({
        title: '',
        description: '',
        due_date: null,
        is_completed: false,
        assigned_workers: [],
        existing_photos: [],
        existing_documents: [],
        linked_forms: [],
        pending_form_templates: [],
        pending_existing_forms: [],
        pending_file_links: [],
        removed_photo_ids: [],
        removed_document_ids: [],
        removed_form_ids: [],
        checklist_items: [],
        ...initialData
      });
    }
    setDialogPhotos([]);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPhotos({});
    setDialogPhotos([]);
    setSubmitting(false);
  };

  const handleDialogPhotoSelect = useCallback(async (event) => {
    const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files);
    if (warning) {
      alert(warning);
      return;
    }

    const validFiles = selectedFiles.filter(file =>
      file.type.startsWith('image/') || file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    
    if (validFiles.length === 0) return;

    const compressedFiles = await compressImages(validFiles);
    const renamedFiles = compressedFiles.map(file => renameFile(file));
    
    const newPhotos = renamedFiles.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      name: file.name
    }));
    
    setDialogPhotos(prev => [...prev, ...newPhotos]);
  }, []);

  const removeDialogPhoto = useCallback((index) => {
    setDialogPhotos(prev => {
      const updated = [...prev];
      if (updated[index]?.preview) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const handleAddPin = useCallback((x, y) => {
    if (!viewerFile) return;
    handleOpenDialog(null, {
      source_file_id: viewerFile.file_id,
      pin_x: x,
      pin_y: y
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerFile]);

  const handleSubmit = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      const activityData = {
        ...formData,
        due_date: formData.due_date ? formData.due_date.toISOString() : null,
      };
      delete activityData.existing_photos;
      delete activityData.existing_documents;
      delete activityData.linked_forms;
      delete activityData.pending_form_templates;
      delete activityData.pending_existing_forms;
      delete activityData.pending_file_links;
      delete activityData.removed_photo_ids;
      delete activityData.removed_document_ids;
      delete activityData.removed_form_ids;

      let activityId;
      if (selectedActivity) {
        await activitiesApi.update(selectedActivity.activity_id, activityData);
        activityId = selectedActivity.activity_id;
      } else {
        const response = await activitiesApi.create(projectId, activityData);
        activityId = response.data.activity_id;
      }

      // Assign workers if any are selected
      const validWorkerIds = (formData.assigned_workers || []).filter(id => id != null);
      if (validWorkerIds.length > 0) {
        await activitiesApi.assignWorkers(activityId, validWorkerIds);
      }

      // Upload photos
      if (dialogPhotos.length > 0) {
        await Promise.all(dialogPhotos.map(photo => {
          const formData = new FormData();
          formData.append('file', photo.file);
          return activitiesApi.uploadPhoto(activityId, formData);
        }));
      }

      if (formData.pending_form_templates?.length > 0) {
        await Promise.all(
          formData.pending_form_templates.map((template) => {
            const initialFormData = {};
            (template.form_schema?.fields || []).forEach((field) => {
              initialFormData[field.id] = '';
            });

            return projectFormsApi.create(projectId, {
              template_id: template.template_id,
              form_data: initialFormData,
              activity_id: activityId,
            });
          })
        );
      }

      if (formData.pending_existing_forms?.length > 0) {
        await Promise.all(
          formData.pending_existing_forms.map((form) =>
            projectFormsApi.linkActivity(projectId, form.project_form_id || form.id, activityId)
          )
        );
      }

      if (formData.pending_file_links?.length > 0) {
        await Promise.all(formData.pending_file_links.map((file) =>
          activitiesApi.linkFile(activityId, file.file_id)
        ));
      }

      if (selectedActivity) {
        const removals = [
          ...(formData.removed_photo_ids || []).map((photoId) => activitiesApi.deletePhoto(photoId)),
          ...(formData.removed_document_ids || []).map((fileId) => {
            const document = selectedActivity.documents?.find((item) => item.file_id === fileId);
            return document?.is_existing_file_link
              ? activitiesApi.unlinkFile(activityId, fileId)
              : filesApi.delete(fileId);
          }),
          ...(formData.removed_form_ids || []).map((formId) => projectFormsApi.unlinkActivity(projectId, formId, activityId)),
        ];

        if (removals.length > 0) {
          await Promise.all(removals);
        }
      }

      handleCloseDialog();
      await Promise.all([
        refreshActivities(),
        loadProjectForms(),
      ]);
      
      // If the ImageViewer is open, refresh the pins to show updated activity
      if (viewerOpen && viewerFile) {
        const sourceFileId = viewerFile.file_id;
        const pinsResponse = await activitiesApi.getByFileId(sourceFileId);
        setViewerPins(pinsResponse.data || []);
        
        // Update the selected pin if it was the one being edited
        if (viewerPin && viewerPin.activity_id === activityId) {
          const updatedPin = pinsResponse.data?.find(p => p.activity_id === activityId);
          if (updatedPin) {
            setViewerPin(updatedPin);
            setRefreshKey(prev => prev + 1);
          }
        }
      }
    } catch (error) {
      console.error('Error saving activity:', error);
      alert(error?.response?.data?.error || 'Failed to save activity. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleComplete = async (activity) => {
    try {
      setCompletingActivity(activity.activity_id);
      if (activity.is_completed) {
        await activitiesApi.markAsIncomplete(activity.activity_id);
      } else {
        await activitiesApi.markAsComplete(activity.activity_id);
      }
      refreshActivities();
    } catch (error) {
      console.error('Error updating activity status:', error);
    } finally {
      setCompletingActivity(null);
    }
  };

  const handleChecklistToggle = useCallback((activity, itemId, isCompleted) => {
    const currentActivity = activities.find(
      (existingActivity) => existingActivity.activity_id === activity.activity_id
    ) || activity;

    const checklistItems = (currentActivity.checklist_items || []).map((item) =>
      item.id === itemId ? { ...item, is_completed: isCompleted } : item
    );

    const payload = {
      checklist_items: checklistItems,
    };

    pendingChecklistSavesRef.current.set(activity.activity_id, payload);
    syncChecklistItemsInState(activity.activity_id, checklistItems);

    void activitiesApi.update(activity.activity_id, payload)
      .then(() => {
        const pendingPayload = pendingChecklistSavesRef.current.get(activity.activity_id);
        if (pendingPayload === payload) {
          pendingChecklistSavesRef.current.delete(activity.activity_id);
        }
      })
      .catch(async (error) => {
        console.error('Error updating activity checklist:', error);

        const pendingPayload = pendingChecklistSavesRef.current.get(activity.activity_id);
        if (pendingPayload !== payload) {
          return;
        }

        try {
          await activitiesApi.updateKeepalive(activity.activity_id, payload);
          if (pendingChecklistSavesRef.current.get(activity.activity_id) === payload) {
            pendingChecklistSavesRef.current.delete(activity.activity_id);
          }
        } catch (backgroundError) {
          console.error('Error background saving activity checklist:', backgroundError);
          pendingChecklistSavesRef.current.delete(activity.activity_id);
          await refreshActivities();
        }
      });
  }, [activities, refreshActivities, syncChecklistItemsInState]);

  const handleDelete = async () => {
    if (!selectedActivity || deleting) return;

    try {
      setDeleting(true);
      await activitiesApi.delete(selectedActivity.activity_id);
      setDeleteDialogOpen(false);
      refreshActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleActivityExpand = useCallback((activityId) => {
    if (expandedActivity === activityId) {
      setExpandedActivity(null);
    } else {
      setExpandedActivity(activityId);
      // Notes and photos are already pre-loaded from the initial query
    }
  }, [expandedActivity]);

  const handlePhotoSelect = useCallback(async (event, activityId) => {
    const files = Array.from(event.target.files);
    
    const validFiles = files.filter(file => {
      if (file.type.startsWith('image/')) {
        if (file.size <= 10 * 1024 * 1024) {
          return true;
        } else {
          console.error('File size must be under 10MB');
          return false;
        }
      } else {
        console.error('Only image files are allowed');
        return false;
      }
    });

    // Compress images before creating previews
    const compressedFiles = await compressImages(validFiles);

    // Rename files with UUID to avoid conflicts
    const renamedFiles = compressedFiles.map(file => renameFile(file));

    const newPhotos = renamedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setSelectedPhotos(prev => ({ 
      ...prev, 
      [activityId]: [...(prev[activityId] || []), ...newPhotos] 
    }));
  }, []);

  const removePhoto = useCallback((activityId, index) => {
    setSelectedPhotos(prev => {
      const activityPhotos = prev[activityId] || [];
      const updated = [...activityPhotos];
      // Revoke the object URL to free memory
      if (updated[index]?.preview) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return { ...prev, [activityId]: updated };
    });
  }, []);

  const handleNoteSubmit = useCallback(async (activityId) => {
    const noteContent = newNotes[activityId] || '';
    const activitySelectedPhotos = selectedPhotos[activityId] || [];
    
    if (!noteContent.trim() && activitySelectedPhotos.length === 0) return;

    try {
      setUploading(prev => ({ ...prev, [activityId]: true }));

      let response;
      if (activitySelectedPhotos.length > 0) {
        const formData = new FormData();
        formData.append('content', noteContent || 'Photo attachment');
        
        activitySelectedPhotos.forEach((photo, index) => {
          formData.append(`photo_${index}`, photo.file);
        });

        response = await activitiesApi.createNoteWithPhotos(activityId, formData);
        
        // Clean up object URLs
        activitySelectedPhotos.forEach(photo => {
          if (photo.preview) {
            URL.revokeObjectURL(photo.preview);
          }
        });
      } else {
        response = await activitiesApi.createNote(activityId, noteContent);
      }
      
      // Clear form state
      setNewNotes(prev => ({ ...prev, [activityId]: '' }));
      setSelectedPhotos(prev => ({ ...prev, [activityId]: [] }));
      
      // Add the new note to the existing list instead of reloading everything
      if (response.data.note) {
        setActivityNotes(prev => ({
          ...prev,
          [activityId]: [...(prev[activityId] || []), response.data.note]
        }));
      }
    } catch (error) {
      console.error('Error creating activity note:', error);
    } finally {
      setUploading(prev => ({ ...prev, [activityId]: false }));
    }
  }, [newNotes, selectedPhotos]);  // Debounce mention search to avoid excessive filtering
  const mentionDebounceRef = useRef({});
  
  const handleMentionInput = useCallback((value, cursorPosition, activityId) => {
    const atIndex = value.lastIndexOf('@', cursorPosition);
    
    if (atIndex === -1) {
      setShowMentions(prev => ({ ...prev, [activityId]: false }));
      return;
    }
    
    if (atIndex === cursorPosition - 1) {
      setShowMentions(prev => ({ ...prev, [activityId]: true }));
      setMentionSuggestions(prev => ({ ...prev, [activityId]: workers }));
      return;
    }
    
    // Debounce the search
    if (mentionDebounceRef.current[activityId]) {
      clearTimeout(mentionDebounceRef.current[activityId]);
    }
    
    mentionDebounceRef.current[activityId] = setTimeout(() => {
      const searchTerm = value.substring(atIndex + 1, cursorPosition).toLowerCase();
      const filtered = workers.filter(worker => 
        worker.name.toLowerCase().includes(searchTerm)
      );
      setMentionSuggestions(prev => ({ ...prev, [activityId]: filtered }));
      setShowMentions(prev => ({ ...prev, [activityId]: filtered.length > 0 }));
    }, 150); // 150ms debounce
  }, [workers]);

  const insertMention = useCallback((worker, activityId) => {
    const currentNote = newNotes[activityId] || '';
    const atIndex = currentNote.lastIndexOf('@');
    const beforeMention = currentNote.substring(0, atIndex);
    const afterMention = currentNote.substring(atIndex).replace(/@\w*/, `@${worker.name.replace(' ', '')}`);
    setNewNotes(prev => ({ ...prev, [activityId]: beforeMention + afterMention + ' ' }));
    setShowMentions(prev => ({ ...prev, [activityId]: false }));
  }, [newNotes]);

  const handleNoteChange = useCallback((activityId, value) => {
    setNewNotes(prev => ({ ...prev, [activityId]: value }));
  }, []);

  const handleViewPlan = async (activity, linkedPlan = null) => {
    const sourceFileId = linkedPlan?.file_id || activity.source_file_id || activity.SourceFileId;
    if (!sourceFileId) return;

    if (linkedPlan?.file_name?.toLowerCase().endsWith('.pdf')) {
      await handleDocumentOpen(linkedPlan);
      return;
    }
    
    try {
      setViewerLoading(true);
      setLoadingPlanActivityId(activity.activity_id);
      
      // 1. Get file URL
      const urlResponse = await filesApi.getUrl(sourceFileId);
      const fileUrl = urlResponse.data.url;
      
      // 2. Get file details (we need to fetch all files to find the name/date)
      // This is a bit inefficient but necessary without a getById endpoint
      const filesResponse = await filesApi.getByProjectId(projectId, 'plans');
      const file = filesResponse.data.find(f => f.file_id === sourceFileId) || linkedPlan;
      
      if (file && fileUrl) {
        // 3. Get all pins for this file
        const pinsResponse = await activitiesApi.getByFileId(sourceFileId);
        
        setViewerFile({
          ...file,
          url: fileUrl
        });
        setViewerPins(pinsResponse.data || []);
        setViewerPin(String(activity.source_file_id || activity.SourceFileId) === String(sourceFileId) ? activity : null);
        setViewerOpen(true);

        // Ensure workers are loaded for mentions in ActivityDetails
        if (!workers || workers.length === 0) {
          console.log('Workers empty in handleViewPlan, reloading...');
          loadWorkers();
        }
      }
    } catch (error) {
      console.error('Error viewing plan:', error);
    } finally {
      setViewerLoading(false);
      setLoadingPlanActivityId(null);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewerFile(null);
    setViewerPin(null);
    setViewerPins([]);
  };

  const handlePinClick = (pin) => {
    setViewerPin(pin);
  };

  // Cache photo URLs to avoid repeated fetches
  const photoUrlCache = useRef({});
  const currentActivityForPreview = useRef(null);

  const getResolvedPhotoUrl = useCallback(async (photo) => {
    if (!photo) {
      return '';
    }

    if (photo.file_id) {
      const key = `file-${photo.file_id}`;
      if (!photoUrlCache.current[key]) {
        const response = await filesApi.getUrl(photo.file_id);
        photoUrlCache.current[key] = response.data.url;
      }
      return photoUrlCache.current[key];
    }

    if (photo.url) {
      return photo.url;
    }

    if (photo.photo_id && photoUrlCache.current[photo.photo_id]) {
      return photoUrlCache.current[photo.photo_id];
    }

    if (!photo.photo_id) {
      return '';
    }

    const response = await activitiesApi.getPhotoUrl(photo.photo_id);
    const url = response.data.url;
    photoUrlCache.current[photo.photo_id] = url;
    return url;
  }, []);
  
  const handlePhotoPreviewWrapper = useCallback(async (photo, activityId = null) => {
    if (photo.open) {
      setPhotoPreview(photo);
      return;
    }
    
    try {
      // Get the activity ID from the passed parameter or from ref
      const targetActivityId = activityId || currentActivityForPreview.current;
      
      // Collect all photos from activity and notes
      const allActivityPhotos = activityPhotos[targetActivityId] || [];
      const activityNotesList = activityNotes[targetActivityId] || [];
      const allNotesPhotos = activityNotesList.reduce((acc, note) => {
        return acc.concat(note.photos || []);
      }, []);
      
      const linkedPhotos = (activities.find((item) => item.activity_id === targetActivityId)?.documents || [])
        .filter((document) => document.file_type === 'photos');
      const allPhotos = [...allActivityPhotos, ...linkedPhotos, ...allNotesPhotos];
      const currentIndex = allPhotos.findIndex(p => 
        (photo.file_id ? p.file_id === photo.file_id : p.photo_id === photo.photo_id)
        || (p.url === photo.url && p.file_name === photo.file_name)
      );
      
      const url = await getResolvedPhotoUrl(photo);
      
      setPhotoPreview({
        open: true,
        url: url,
        title: photo.file_name,
        allPhotos,
        currentIndex: currentIndex >= 0 ? currentIndex : 0
      });
    } catch (error) {
      console.error('Error loading photo preview:', error);
    }
  }, [activities, activityPhotos, activityNotes, getResolvedPhotoUrl]);

  const handleDocumentOpen = useCallback(async (document) => {
    let pendingWindow = null;
    try {
      const isMobileBrowser = typeof window !== 'undefined'
        && /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent || '');
      if (isMobileBrowser) {
        pendingWindow = window.open('', '_blank');
      }

      const response = await filesApi.getUrl(document.file_id);
      const fileUrl = response.data.url;
      if (pendingWindow) {
        pendingWindow.location.href = fileUrl;
        pendingWindow.opener = null;
        return;
      }

      const newWindow = window.open(fileUrl, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    } catch (error) {
      if (pendingWindow && !pendingWindow.closed) {
        pendingWindow.close();
      }
      console.error('Error opening activity document:', error);
    }
  }, []);

  const handlePhotoNavigate = useCallback(async (direction) => {
    if (!photoPreview.allPhotos || photoPreview.allPhotos.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (photoPreview.currentIndex + 1) % photoPreview.allPhotos.length;
    } else {
      newIndex = (photoPreview.currentIndex - 1 + photoPreview.allPhotos.length) % photoPreview.allPhotos.length;
    }
    
    const newPhoto = photoPreview.allPhotos[newIndex];

    try {
      const url = await getResolvedPhotoUrl(newPhoto);
      setPhotoPreview(prev => ({
        ...prev,
        url,
        title: newPhoto.file_name,
        currentIndex: newIndex
      }));
    } catch (error) {
      console.error('Error navigating activity photo preview:', error);
    }
  }, [getResolvedPhotoUrl, photoPreview]);
  
  // Cleanup debounce timers on unmount
  useEffect(() => {
    const debounceRef = mentionDebounceRef.current;
    const photosRef = selectedPhotos;
    return () => {
      // Clean up debounce timers
      Object.values(debounceRef).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      
      // Clean up any object URLs
      Object.values(photosRef).forEach(photos => {
        photos?.forEach(photo => {
          if (photo.preview) {
            URL.revokeObjectURL(photo.preview);
          }
        });
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 1, md: 2 }, overflowX: 'hidden' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            size={isMobile ? 'medium' : 'medium'}
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              borderRadius: 2,
              px: 3
            }}
          >
            Add Activity
          </Button>
          
        </Box>

        {/* Activities List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : activities.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 6,
              textAlign: 'center'
            }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                No activities yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first activity to get started
              </Typography>
            </Box>
          ) : (
            [...activities].sort(byDueDate).map((activity) => {
              // Store the current activity for photo preview navigation
              if (expandedActivity === activity.activity_id) {
                currentActivityForPreview.current = activity.activity_id;
              }
              return (
              <ActivityCard 
                key={activity.activity_id} 
                activity={activity}
                expandedActivity={expandedActivity}
                activityNotes={activityNotes}
                activityPhotos={activityPhotos}
                newNotes={newNotes}
                selectedPhotos={selectedPhotos}
                showMentions={showMentions}
                mentionSuggestions={mentionSuggestions}
                workers={workers}
                uploading={uploading}
                loadingNotes={loadingNotes}
                loadingPhotos={loadingPhotos}
                completingActivity={completingActivity}
                onToggleComplete={handleToggleComplete}
                onEdit={handleOpenDialog}
                onDelete={(activity) => { setSelectedActivity(activity); setDeleteDialogOpen(true); }}
                onActivityExpand={handleActivityExpand}
                 onPhotoPreview={handlePhotoPreviewWrapper}
                 onDocumentOpen={handleDocumentOpen}
                 onNoteChange={handleNoteChange}
                onMentionInput={handleMentionInput}
                 onPhotoSelect={handlePhotoSelect}
                 onNoteSubmit={handleNoteSubmit}
                 onRemovePhoto={removePhoto}
                 onInsertMention={insertMention}
                 onChecklistToggle={handleChecklistToggle}
                 onViewPlan={handleViewPlan}
                 isPlanLoading={loadingPlanActivityId === activity.activity_id}
                 onOpenForm={onOpenForm}
              />
            );
            })
          )}
        </Box>

        {/* Plan Viewer */}
        {viewerFile && (
          <ImageViewer
            open={viewerOpen}
            onClose={handleCloseViewer}
            imageUrl={viewerFile.url}
            fileName={viewerFile.file_name}
            uploadedDate={viewerFile.uploaded_date}
            allImages={[viewerFile]} // Only show this file
            currentIndex={0}
            pins={viewerPins}
            enablePinMode={pinMode}
            onTogglePinMode={() => setPinMode(!pinMode)}
            onAddPin={handleAddPin}
            isPlanImage={true}
            selectedPin={viewerPin}
            onClosePinDetails={() => setViewerPin(null)}
            onPinClick={handlePinClick}
            workers={workers}
            onEditActivity={(activity) => {
              // Keep viewer open, just open dialog
              handleOpenDialog(activity);
            }}
            onDeleteActivity={(activity) => {
              // Keep viewer open
              setSelectedActivity(activity);
              setDeleteDialogOpen(true);
            }}
            onOpenPlan={handleViewPlan}
            refreshKey={refreshKey}
          />
        )}

        {/* Activity Dialog */}
        <ActivityDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
          formData={formData}
          onFormDataChange={setFormData}
          workers={workers}
          formTemplates={formTemplates}
          projectForms={projectForms}
          projectFiles={projectFiles}
          documentFolders={documentFolders}
          projectFilesLoading={projectFilesLoading}
          photos={dialogPhotos}
          onPhotoSelect={handleDialogPhotoSelect}
          onPhotoRemove={removeDialogPhoto}
          submitting={submitting}
          isEditing={!!selectedActivity}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)} 
          fullScreen={isMobile}
          PaperProps={{
            sx: { borderRadius: isMobile ? 0 : 3 }
          }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Delete Activity</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this activity? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 2
              }}
            >
              Cancel
            </Button>
            <Button 
              color="error" 
              variant="contained"
              onClick={handleDelete}
              disabled={deleting}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2
              }}
            >
              {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Photo Preview using ImageViewer */}
        <ImageViewer
          open={photoPreview.open}
          onClose={() => setPhotoPreview({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 })}
          imageUrl={photoPreview.url}
          fileName={photoPreview.title}
          allImages={photoPreview.allPhotos || []}
          currentIndex={photoPreview.currentIndex || 0}
          onNavigate={handlePhotoNavigate}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default ProjectActivitiesTab;
